
import { useState, useEffect, useCallback } from 'react';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  deleteApiKey,
  ApiKey,
} from '../../../utils/apiKeys';

export default function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showNewKeyValue, setShowNewKeyValue] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    const data = await listApiKeys();
    setKeys(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError('');
    const created = await createApiKey(newKeyName.trim());
    if (created) {
      setShowNewKeyValue(created.key_value);
      setNewKeyName('');
      await fetchKeys();
    } else {
      setError('Failed to create API key. Please try again.');
    }
    setCreating(false);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleRevoke = async (id: string) => {
    setActionLoading(id);
    await revokeApiKey(id);
    await fetchKeys();
    setActionLoading(null);
  };

  const handleDelete = async (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setActionLoading(id);
    await deleteApiKey(id);
    setConfirmDeleteId(null);
    await fetchKeys();
    setActionLoading(null);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-8">

      {/* ── Create New Key ── */}
      <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
        <h2 className="text-lg font-bold mb-1">Create New API Key</h2>
        <p className="text-gray-400 text-sm mb-5">
          Give your key a descriptive name so you remember where it's used.
        </p>
        <form onSubmit={handleCreate} className="flex gap-3">
          <input
            type="text"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            placeholder="e.g. Zapier Integration, My Dashboard..."
            className="flex-1 px-4 py-2.5 bg-[#0a1628] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 text-sm transition-colors"
            maxLength={80}
          />
          <button
            type="submit"
            disabled={creating || !newKeyName.trim()}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            {creating ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i> Creating…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <i className="ri-add-line"></i> Generate Key
              </span>
            )}
          </button>
        </form>
        {error && (
          <p className="mt-3 text-red-400 text-sm flex items-center gap-1">
            <i className="ri-error-warning-line"></i> {error}
          </p>
        )}
      </div>

      {/* ── New Key Banner ── */}
      {showNewKeyValue && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center text-green-400 flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-green-300 mb-1">API Key Created — Copy it now!</p>
              <p className="text-green-400/70 text-xs mb-3">
                This is the only time the full key will be shown. Store it somewhere safe.
              </p>
              <div className="flex items-center gap-2 bg-[#0a1628] rounded-lg px-4 py-3 border border-green-500/30">
                <code className="flex-1 text-green-300 text-sm font-mono break-all">{showNewKeyValue}</code>
                <button
                  onClick={() => handleCopy(showNewKeyValue, 'new')}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-green-400 hover:text-white transition-colors cursor-pointer"
                  title="Copy key"
                >
                  <i className={copiedId === 'new' ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowNewKeyValue(null)}
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── API Usage Info ── */}
      <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <i className="ri-code-s-slash-line text-gray-400"></i> How to Use Your API Key
        </h2>
        <div className="space-y-3 text-sm text-gray-400">
          <p>Add your key as a URL parameter when calling the analytics API:</p>
          <div className="bg-[#0a1628] rounded-lg px-4 py-3 border border-gray-700 font-mono text-xs text-gray-300 break-all">
            GET /api/analytics?api_key=YOUR_KEY_HERE
          </div>
          <p>Optional date filtering:</p>
          <div className="bg-[#0a1628] rounded-lg px-4 py-3 border border-gray-700 font-mono text-xs text-gray-300 break-all">
            GET /api/analytics?api_key=YOUR_KEY&amp;start=2024-01-01&amp;end=2024-12-31
          </div>
        </div>
      </div>

      {/* ── Keys List ── */}
      <div className="bg-[#0f1f3a] rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-bold">Your API Keys</h2>
          <button
            onClick={fetchKeys}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
            title="Refresh"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <i className="ri-loader-4-line animate-spin text-2xl mb-2 block"></i>
            Loading keys…
          </div>
        ) : keys.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <i className="ri-key-2-line text-3xl mb-2 block text-gray-600"></i>
            No API keys yet. Create one above.
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {keys.map(key => (
              <div key={key.id} className="px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white">{key.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      key.is_active
                        ? 'bg-green-500/15 text-green-400'
                        : 'bg-gray-600/30 text-gray-500'
                    }`}>
                      {key.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-gray-400 font-mono">
                      {key.key_value.slice(0, 8)}••••••••••••••••••••••••••••••••
                    </code>
                    {key.is_active && (
                      <button
                        onClick={() => handleCopy(key.key_value, key.id)}
                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
                        title="Copy full key"
                      >
                        <i className={`text-xs ${copiedId === key.id ? 'ri-check-line text-green-400' : 'ri-clipboard-line'}`}></i>
                      </button>
                    )}
                  </div>
                  <div className="flex gap-4 mt-1.5 text-xs text-gray-600">
                    <span>Created {formatDate(key.created_at)}</span>
                    <span>Last used: {formatDate(key.last_used_at)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {key.is_active && (
                    <button
                      onClick={() => handleRevoke(key.id)}
                      disabled={actionLoading === key.id}
                      className="px-3 py-1.5 bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {actionLoading === key.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <><i className="ri-forbid-line mr-1"></i>Revoke</>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(key.id)}
                    disabled={actionLoading === key.id}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 ${
                      confirmDeleteId === key.id
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-red-600/10 hover:bg-red-600/30 text-red-400'
                    }`}
                    onBlur={() => setConfirmDeleteId(null)}
                  >
                    {actionLoading === key.id ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : confirmDeleteId === key.id ? (
                      <><i className="ri-alert-line mr-1"></i>Confirm Delete</>
                    ) : (
                      <><i className="ri-delete-bin-line mr-1"></i>Delete</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
